import {
  MAX_FILE_UPLOAD_SIZE_BYTES,
  MAX_FILE_UPLOAD_SIZE_MB,
  FILE_UPLOAD_SIZE_EXCEEDED_MESSAGE,
  FILE_UPLOAD_BATCH_SIZE_EXCEEDED_MESSAGE,
} from '../Global/Constants';

export {
  MAX_FILE_UPLOAD_SIZE_BYTES,
  MAX_FILE_UPLOAD_SIZE_MB,
  FILE_UPLOAD_SIZE_EXCEEDED_MESSAGE,
  FILE_UPLOAD_BATCH_SIZE_EXCEEDED_MESSAGE,
};

/**
 * Returns total size in bytes for a FileList, File array, or file-like objects with a size property.
 */
export const getFilesTotalSize = (files) => {
  if (!files || files.length === 0) {
    return 0;
  }
  return Array.from(files).reduce(
    (sum, file) => sum + (file && file.size ? Number(file.size) : 0),
    0
  );
};

/**
 * Returns total size in bytes for already-uploaded file metadata objects.
 */
export const getExistingFilesTotalSize = (existingFiles) => {
  if (!existingFiles || existingFiles.length === 0) {
    return 0;
  }
  return existingFiles.reduce(
    (sum, file) => sum + (file && file.size ? Number(file.size) : 0),
    0
  );
};

/**
 * Validates per-file and combined upload size limits.
 * - Each file must be <= 25 MB
 * - All files in the selection must total <= 25 MB
 * - When existingFiles is provided, new + existing must total <= 25 MB
 *
 * @param {FileList|File[]|Object} files
 * @param {{ existingFiles?: Array }} [options]
 * @returns {{ valid: boolean, errorMsg: string|null }}
 */
export const validateFilesWithinSizeLimit = (files, options = {}) => {
  const { existingFiles = [] } = options;

  if (!files || files.length === 0) {
    return { valid: true, errorMsg: null };
  }

  const fileList = Array.from(files);
  const oversized = fileList.find(
    (file) => file && file.size > MAX_FILE_UPLOAD_SIZE_BYTES
  );

  if (oversized) {
    return { valid: false, errorMsg: FILE_UPLOAD_SIZE_EXCEEDED_MESSAGE };
  }

  const newFilesTotal = getFilesTotalSize(fileList);
  if (newFilesTotal > MAX_FILE_UPLOAD_SIZE_BYTES) {
    return { valid: false, errorMsg: FILE_UPLOAD_BATCH_SIZE_EXCEEDED_MESSAGE };
  }

  if (existingFiles && existingFiles.length > 0) {
    const combinedTotal = newFilesTotal + getExistingFilesTotalSize(existingFiles);
    if (combinedTotal > MAX_FILE_UPLOAD_SIZE_BYTES) {
      return { valid: false, errorMsg: FILE_UPLOAD_BATCH_SIZE_EXCEEDED_MESSAGE };
    }
  }

  return { valid: true, errorMsg: null };
};

/**
 * Reports file size validation errors via onError, failMessage, or console.
 */
export const reportFileSizeValidationError = (errorMsg, { onError, failMessage } = {}) => {
  if (onError) {
    onError(errorMsg);
  } else if (failMessage) {
    failMessage(errorMsg);
  } else {
    console.error(errorMsg);
  }
};
